import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Login.css'; // Reuse login styles

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await apiService.forgotPassword(email.trim());
      setMessage(response.message);
      setSubmitted(true);
    } catch (error: any) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Check Your Email</h1>
            <p>Password reset instructions have been sent</p>
          </div>

          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Reset Email Sent</h3>
            <p>{message}</p>
            <p>Please check your email inbox and follow the instructions to reset your password.</p>
          </div>

          <div className="form-footer">
            <p>Didn't receive the email?</p>
            <button 
              onClick={() => {
                setSubmitted(false);
                setMessage('');
                setEmail('');
              }}
              className="link-button"
            >
              Try again
            </button>
          </div>

          <div className="login-footer">
            <Link to="/login" className="link-button">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Reset Your Password</h1>
          <p>Enter your email address and we'll send you a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              placeholder="Enter your email address"
              required
              autoComplete="email"
              className={error ? 'error' : ''}
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon"><strong>⚠</strong></span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="login-button"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>

          <div className="login-footer">
            <Link to="/login" className="link-button">
              ← Back to Login
            </Link>
            
            <div className="signup-link">
              Don't have an account?{' '}
              <Link to="/signup">Create one here</Link>
            </div>
          </div>
        </form>

        {/* Security Information */}
        <div className="security-info">
          <h3>🔒 Security Notice</h3>
          <ul>
            <li>Reset links expire in 24 hours</li>
            <li>You can only use each reset link once</li>
            <li>If you don't receive an email, check your spam folder</li>
            <li>Your current password remains active until you complete the reset</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;